import { useImmerAtom } from 'jotai-immer';
import { editRestrictionModelAtom } from '../edit/mfr_edit_restrictions_atoms';
import { AdminMFRRestrictionModel } from './mfr_edit_restrictions_types';
import { useCallback, useEffect } from 'react';
import { modelVM } from './mfr_restrictions_models';
import { MFRRestrictionsDataItem } from '../mfr_restrictions_list_types';
import apiSvc from '../../../../core/services/apiSvc';
import validSvc from '../../../../core/validSvc';
import utilSvc from '../../../../core/utilSvc';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { MFR_RESTRICTIONS_SAVE } from '../../mfr_restrictions_const';
import { mfrFacilityTypeOptions } from './mfr_restrictions_edit_dropdown_options';
import { validateFieldByLength } from '../../../../core/components/validation/validators';
import { ValidPropMultiSelect } from '../../../../core/components/validation/ValidInputMultiSelect';

const validateFacilityTypes = (prop: ValidPropMultiSelect): boolean => {
    if (prop.val.length === 0) {
        prop.err = 'Required';
        return false;
    } else {
        prop.err = '';
    }
    return true;
};

type UseEditRestrictionsModelReturn = {
    model: AdminMFRRestrictionModel;
    submit: (callback?: () => void) => void;
    setPropText: (key: string, value: any) => void;
};

export const useEditRestrictionsModel = (
    baseModels: Record<string, MFRRestrictionsDataItem>,
): UseEditRestrictionsModelReturn => {
    const [model, setModel] = useImmerAtom(editRestrictionModelAtom);

    useEffect(() => {
        if (baseModels && Object.keys(baseModels).length === 1) {
            const firstEntry = baseModels[Object.keys(baseModels)[0]];
            setModel(() => modelVM(firstEntry));
        } else if (baseModels && Object.keys(baseModels).length === 0) {
            setModel(() => modelVM());
        }
    }, [baseModels, setModel]);

    const validate = (): boolean => {
        let result = true;

        setModel(draft => {
            const parentCoIsValid = validateFieldByLength({
                prop: draft.ParentCo,
                minLength: 3,
                maxLength: 50,
                required: true,
            });

            const facilityTypesIsValid = validateFacilityTypes(draft.CeRestrictTypes);

            if (!parentCoIsValid || !facilityTypesIsValid) {
                result = false;
            }
        });
        return result;
    };

    const submit = async (callback?: () => void): Promise<void> => {
        const EditIds: string[] = [];
        Object.keys(baseModels).forEach(key => {
            EditIds.push(key);
        });

        if (EditIds.length === 0) {
            return;
        }

        if (!validate()) {
            return;
        }

        const params = {
            Params: {
                EditIds,
                ParentCo: model.ParentCo.val,
                CeRestrictTypes:
                    model.CeRestrictTypes.val.length === mfrFacilityTypeOptions.length
                        ? ['ALL']
                        : model.CeRestrictTypes.val,
                ExemptStates: model.ExemptStates.val,
            },
        };

        apiSvc.post(MFR_RESTRICTIONS_SAVE, params).then(
            res => {
                // bad upsert response format
                if (!validSvc.httpUpsert200Resp(res)) {
                    ngDelegate.warn(
                        'Bad 200 api response format',
                        MFR_RESTRICTIONS_SAVE + 'repo',
                        utilSvc.http.safeResponse(res),
                    );
                    return;
                }

                if (callback) {
                    callback();
                }

                ngDelegate.ok('Restriction(s) saved');
            },
            res => {
                if (res === null || res.status === 409 || res.status === 422) {
                    // already handled upstream
                    return;
                } else if (!validSvc.httpUpsertErrResp(res)) {
                    // bad error response format
                    ngDelegate.warn(
                        'Api request failed with unknown error response format',
                        MFR_RESTRICTIONS_SAVE + 'repo',
                        utilSvc.http.safeResponse(res),
                    );
                } else {
                    ngDelegate.warn(
                        'Api request unexpected error',
                        MFR_RESTRICTIONS_SAVE + 'repo',
                        utilSvc.http.safeResponse(res),
                    );
                    return;
                }
            },
        );
    };

    const setPropText = useCallback(
        (key: string, value: any): void => {
            if (!model.hasOwnProperty(key)) {
                throw new Error(`Property ${key} does not exist on the model`);
            }

            let val = value;

            switch (key) {
                case 'ParentCo':
                    setModel(draft => {
                        draft[key].val = val;
                    });
                    break;
                case 'CeRestrictTypes':
                    if (value === 'ALL') {
                        val = [];
                    }
                    setModel(draft => {
                        draft[key].val = val;
                    });
                    break;
                case 'ExemptStates':
                    if (value === 'NONE') {
                        val = [];
                    }
                    setModel(draft => {
                        draft[key].val = val;
                    });
                    break;
                default:
                    throw new Error(`Property ${key} is not editable`);
            }
        },
        [model, setModel],
    );

    return {
        model,
        submit,
        setPropText,
    };
};
